import type { CartesianPose } from '@sb/geometry';
import { quaternionMultiply } from '@sb/geometry';
import {
  BuiltInFrame,
  type Frame,
  type Orientation,
} from '@sb/messenger/auto_generated/messenger/trajectory_controller_service';

import type { VelocityExpressedIn } from '../types';

import { toMessengerQuaternion } from './messengerQuaternion';

/** Identity in the wire quaternion's own `{x,y,z,w}` shape. */
const IDENTITY_WIRE_ROTATION = { x: 0, y: 0, z: 0, w: 1 };

const IDENTITY_WIRE_POSE = {
  position: { x: 0, y: 0, z: 0 },
  orientation: IDENTITY_WIRE_ROTATION,
};

/**
 * The three frame slots every TC Cartesian primitive carries. `SpeedL` and
 * `ServoL` declare them identically, so one composer serves both.
 */
export interface CartesianFrames {
  reference: Frame;
  commandedFrame: Frame;
  velocityExpressedIn: Orientation;
}

/**
 * The `commanded_frame` slot shared by every TC Cartesian primitive: the
 * controlled point, given as the full flange→TCP transform off the FLANGE
 * built-in frame. Identity means the flange itself.
 *
 * One definition because SpeedL and ServoL must agree on where the TCP is — a
 * jog and a stream that disagreed would move the arm to different places for
 * the same commanded pose.
 */
export function composeCommandedFrame(flangeToTcp: CartesianPose): Frame {
  return {
    parent: BuiltInFrame.BUILT_IN_FRAME_FLANGE,
    poseInParent: {
      position: { x: flangeToTcp.x, y: flangeToTcp.y, z: flangeToTcp.z },
      orientation: toMessengerQuaternion(flangeToTcp),
    },
  };
}

// Wire frames for each `parent` value. `reference` anchors the motion.
// `velocityExpressedIn` parents the axes. The record is exhaustive — the
// compiler rejects a new parent value until it gets a row here.
const BUILT_IN_FRAMES: Record<
  VelocityExpressedIn['parent'],
  { reference: BuiltInFrame; velocityExpressedIn: BuiltInFrame }
> = {
  world: {
    reference: BuiltInFrame.BUILT_IN_FRAME_WORLD,
    velocityExpressedIn: BuiltInFrame.BUILT_IN_FRAME_WORLD,
  },
  base: {
    reference: BuiltInFrame.BUILT_IN_FRAME_BASE,
    velocityExpressedIn: BuiltInFrame.BUILT_IN_FRAME_BASE,
  },
  tcp: {
    reference: BuiltInFrame.BUILT_IN_FRAME_WORLD,
    velocityExpressedIn: BuiltInFrame.BUILT_IN_FRAME_FLANGE,
  },
};

/**
 * Compose the Cartesian frame fields botman owns, for SpeedL's jog direction
 * and ServoL's streamed pose and twist alike:
 *
 * - `reference`: the frame the motion is anchored to — BASE for a base jog or
 *   a base-relative stream, WORLD for the others. The TC validates it but does
 *   not resolve it. The two choices diverge only while the base moves.
 * - `commanded_frame`: the controlled point, as the full flange-to-TCP
 *   transform. Its position sets the pivot for rotational jogs and the point
 *   the TCP-linear speed limit binds to.
 * - `velocity_expressed_in`: the axes a direction or streamed twist resolves
 *   along. For parent 'tcp', the wire parent is FLANGE and the flange-to-TCP
 *   rotation is composed in front of `rotationInParent`, so the axes are the
 *   displayed tooltip axes and not the bare flange axes.
 */
export function composeCartesianFrames(
  axes: VelocityExpressedIn,
  flangeToTcp: CartesianPose,
): CartesianFrames {
  const { parent, rotationInParent } = axes;
  const wireFrames = BUILT_IN_FRAMES[parent];

  // The wire has no TCP frame: lower 'tcp' to FLANGE plus the
  // flange-to-TCP rotation.
  const wireRotation =
    parent === 'tcp'
      ? quaternionMultiply(flangeToTcp, rotationInParent)
      : rotationInParent;

  return {
    reference: {
      parent: wireFrames.reference,
      poseInParent: IDENTITY_WIRE_POSE,
    },
    commandedFrame: composeCommandedFrame(flangeToTcp),
    velocityExpressedIn: {
      parent: wireFrames.velocityExpressedIn,
      rotationInParent: toMessengerQuaternion(wireRotation),
    },
  };
}
